import React, { useEffect, useState } from "react";
import {
  Stack,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  Button,
  Modal,
  TextField,
  CircularProgress,
  Snackbar,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TablePagination,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchMembersData,
  saveMembersToBackend,
  deleteMember,
} from "../../redux/slice/membersSlice";
import { Delete as DeleteIcon, Edit } from "@mui/icons-material";
import { SlideshowLightbox } from "lightbox.js-react";

const Members = () => {
  const dispatch = useDispatch();
  const membersData = useSelector((state) => state.members) || {};
  const { members = [], status, error } = membersData;
  const [open, setOpen] = useState(false);
  const [currentMember, setCurrentMember] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    post: "",
    location: "",
    images: [],
  });
  const [imagePreviews, setImagePreviews] = useState([]);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [memberToDelete, setMemberToDelete] = useState(null);
  const [showLoader, setShowLoader] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  useEffect(() => {
    dispatch(fetchMembersData());
  }, [dispatch]);

  useEffect(() => {
    if (error) {
      setSnackbarMessage(error.message || "An error occurred.");
      setSnackbarOpen(true);
    }
  }, [error]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowLoader(false);
    }, 1000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    return () => {
      imagePreviews.forEach((preview) => {
        if (preview.startsWith("blob:")) {
          URL.revokeObjectURL(preview);
        }
      });
    };
  }, [imagePreviews]);

  const handleOpen = (member = null) => {
    setCurrentMember(member);
    if (member) {
      setFormData({
        name: member.name,
        post: member.post,
        location: member.location,
        images: member.images,
      });
      setImagePreviews(member.images.map((img) => img.url));
    } else {
      setFormData({ name: "", post: "", location: "", images: [] });
      setImagePreviews([]);
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setCurrentMember(null);
    setImagePreviews([]);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = files.filter(
      (file) => file.type.startsWith("image/") && file.size < 5 * 1024 * 1024
    );
    if (validFiles.length !== files.length) {
      setSnackbarMessage("Only images under 5MB are allowed.");
      setSnackbarOpen(true);
    }
    setFormData((prev) => ({ ...prev, images: validFiles }));
    setImagePreviews(validFiles.map((file) => URL.createObjectURL(file)));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.post || !formData.location) {
      setSnackbarMessage("All fields are required.");
      setSnackbarOpen(true);
      return;
    }
    setShowLoader(true);
    try {
      const dataToSubmit = {
        ...formData,
        images: formData.images,
      };
      if (currentMember) {
        await dispatch(
          saveMembersToBackend({
            id: currentMember._id,
            membersData: dataToSubmit,
          })
        );
        setSnackbarMessage("Member updated successfully!");
      } else {
        await dispatch(saveMembersToBackend({ membersData: dataToSubmit }));
        setSnackbarMessage("Member added successfully!");
      }
      setSnackbarOpen(true);
      handleClose();
    } catch (err) {
      setSnackbarMessage(
        err.message || "Failed to save member. Please try again."
      );
      setSnackbarOpen(true);
    } finally {
      setShowLoader(false);
    }
  };

  const handleDelete = (id) => {
    setMemberToDelete(id);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (memberToDelete) {
      setShowLoader(true);
      try {
        await dispatch(deleteMember(memberToDelete));
        setSnackbarMessage("Member deleted successfully!");
        setSnackbarOpen(true);
      } catch (err) {
        setSnackbarMessage(
          err.message || "Failed to delete member. Please try again."
        );
        setSnackbarOpen(true);
      } finally {
        setShowLoader(false);
        setDeleteDialogOpen(false);
        setMemberToDelete(null);
      }
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedMembers = Array.isArray(members)
    ? members.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
    : [];

  if (status === "loading" || showLoader) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="50vh"
      >
        <CircularProgress sx={{ color: "#F68633" }} />
      </Box>
    );
  }

  if (error && !Array.isArray(members)) {
    return (
      <Typography variant="h6" color="error">
        Error: {error.message || "Failed to load members."}
      </Typography>
    );
  }

  return (
    <Box>
      <Typography variant="h4" fontWeight="bold" gutterBottom>
        Members
      </Typography>
      <Button
        variant="contained"
        sx={{
          backgroundColor: "#e0752d",
          "&:hover": { backgroundColor: "#F68633" },
          textTransform: "none",
          mb: 2,
        }}
        onClick={() => handleOpen()}
      >
        Add Member
      </Button>

      {paginatedMembers.length > 0 ? (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Post</TableCell>
                <TableCell>Location</TableCell>
                <TableCell>Images</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedMembers.map((member) => (
                <TableRow key={member._id}>
                  <TableCell>{member.name}</TableCell>
                  <TableCell>{member.post}</TableCell>
                  <TableCell>{member.location}</TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1}>
                      {(member.images || []).map((image, index) => (
                        <SlideshowLightbox key={index}>
                          <img
                            src={`${
                              process.env.REACT_APP_BASE_URL_ASSETS || ""
                            }${image.url}`}
                            alt={member.name}
                            style={{ width: "100px", height: "auto" }}
                          />
                        </SlideshowLightbox>
                      ))}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Button
                      color="primary"
                      size="small"
                      onClick={() => handleOpen(member)}
                    >
                      <Edit />
                    </Button>
                    <Button
                      color="secondary"
                      size="small"
                      sx={{ marginLeft: 1 }}
                      onClick={() => handleDelete(member._id)}
                    >
                      <DeleteIcon fontSize="small" color="error" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Post</TableCell>
              <TableCell>Location</TableCell>
              <TableCell>Images</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell colSpan={5} align="center">
                <Typography>
                  No members found.{" "}
                  <Button onClick={() => handleOpen()} size="small">
                    Add Member
                  </Button>
                </Typography>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      )}

      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={Array.isArray(members) ? members.length : 0}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />

      {/* Modal for Adding/Editing Member */}
      <Modal open={open} onClose={handleClose}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 400,
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
          }}
        >
          <Typography variant="h6" gutterBottom>
            {currentMember ? "Edit Member" : "Add Member"}
          </Typography>
          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              margin="normal"
              label="Name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
            />
            <TextField
              fullWidth
              margin="normal"
              label="Post"
              name="post"
              value={formData.post}
              onChange={handleChange}
              required
            />
            <TextField
              fullWidth
              margin="normal"
              label="Location"
              name="location"
              value={formData.location}
              onChange={handleChange}
              required
            />
            <input
              type="file"
              multiple
              onChange={handleImageChange}
              accept="image/*"
              style={{ marginTop: 16 }}
            />
            <Stack direction="row" spacing={1} sx={{ marginTop: 2 }}>
              {imagePreviews.map((preview, index) => (
                <Avatar
                  key={index}
                  src={
                    typeof preview === "string" && preview.startsWith("blob:")
                      ? preview
                      : `${
                          process.env.REACT_APP_BASE_URL_ASSETS || ""
                        }${preview}`
                  }
                  alt={`preview-${index}`}
                  sx={{ width: 40, height: 40 }}
                />
              ))}
            </Stack>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={showLoader}
              sx={{
                backgroundColor: "#e0752d",
                "&:hover": { backgroundColor: "#F68633" },
                textTransform: "none",
                mt: 2,
              }}
            >
              {showLoader ? (
                <CircularProgress size={24} />
              ) : currentMember ? (
                "Update"
              ) : (
                "Create"
              )}
            </Button>
          </form>
        </Box>
      </Modal>

      {/* Confirmation Dialog for Deletion */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this member? This action cannot be
            undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} color="primary">
            Cancel
          </Button>
          <Button onClick={confirmDelete} color="error" disabled={showLoader}>
            {showLoader ? <CircularProgress size={20} /> : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for Notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        message={snackbarMessage}
      />
    </Box>
  );
};

export default Members;
